import moment from 'moment/moment';
import { localeData } from '../../../locales/data';
import React, { useCallback, useContext, useMemo } from 'react';
import Cookies from 'js-cookie';
import { formatDate } from '../../helpers';

export const SUPPORTED_BROWSER_LANGUAGE_CODES = {
  chineseTraditional: 'zh-tw',
  chineseSimplified: 'zh-cn',
  english: 'en',
  korean: 'ko',
  vietnamese: 'vi',
};

const DEFAULT_LANGUAGE = SUPPORTED_BROWSER_LANGUAGE_CODES.english;

const getMessages = (localeDataObject: any, languageCodeObject = false, localeObject = false) => {
  if (!localeDataObject) {
    return undefined;
  } else if (languageCodeObject) {
    return languageCodeObject;
  } else if (localeObject) {
    return localeObject;
  }

  return localeDataObject[DEFAULT_LANGUAGE];
};

const SUPPORTED_LANGUAGE_SET = new Set<string>(Object.values(SUPPORTED_BROWSER_LANGUAGE_CODES));

// Normalize a raw language tag to one of the 5 supported codes, or undefined if unsupported.
// zh-tw/zh-cn are kept whole; everything else is stripped to its primary subtag (e.g. en-US -> en).
const normalizeToSupportedLanguage = (raw: string | null | undefined): string | undefined => {
  if (!raw) {
    return undefined;
  }
  const lower = raw.toLowerCase();
  const normalized =
    lower === SUPPORTED_BROWSER_LANGUAGE_CODES.chineseTraditional || lower === SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified
      ? lower
      : lower.split(/[_-]+/)[0];
  return SUPPORTED_LANGUAGE_SET.has(normalized) ? normalized : undefined;
};

const getBrowserLanguage = (): string | undefined => {
  const navigatorLanguage = navigator as Navigator & { userLanguage?: string };
  return navigatorLanguage?.languages?.[0] ?? navigatorLanguage.language ?? navigatorLanguage.userLanguage;
};

declare global {
  interface Window {
    __LANGUAGE__?: string;
  }
}

let APP_LANGUAGE: string | undefined = undefined;
const initLanguage = () => {
  // Prefer the explicit choice then the browser; fall back to English.
  const rawLanguage = Cookies.get('__LANGUAGE__') ?? getBrowserLanguage() ?? '';

  // initialize moment with English for consistency across app
  moment.locale(DEFAULT_LANGUAGE);

  APP_LANGUAGE = normalizeToSupportedLanguage(rawLanguage) ?? DEFAULT_LANGUAGE;
  return APP_LANGUAGE;
};
// Returns the currently active language (resolving via cookie → browser → English on first call).
// Used by the fetch interceptor to inject Accept-Language on every API request.
export const getLanguage = (): string => APP_LANGUAGE || initLanguage();

export const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: SUPPORTED_BROWSER_LANGUAGE_CODES.english, label: 'English' },
  { value: SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified, label: '简体中文 (Simplified Chinese)' },
  { value: SUPPORTED_BROWSER_LANGUAGE_CODES.chineseTraditional, label: '繁體中文 (Traditional Chinese)' },
  { value: SUPPORTED_BROWSER_LANGUAGE_CODES.vietnamese, label: 'Tiếng Việt (Vietnamese)' },
  { value: SUPPORTED_BROWSER_LANGUAGE_CODES.korean, label: '한국어 (Korean)' },
];

// ISO-2 country-> language, used as a fallback when neither account nor browser yields a supported code.
export const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  CN: SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified,
  TW: SUPPORTED_BROWSER_LANGUAGE_CODES.chineseTraditional,
  HK: SUPPORTED_BROWSER_LANGUAGE_CODES.chineseTraditional,
  KR: SUPPORTED_BROWSER_LANGUAGE_CODES.korean,
  VN: SUPPORTED_BROWSER_LANGUAGE_CODES.vietnamese,
};

// Resolve the active language via account → browser → country → English, each step used only if it
// yields one of the 5 supported codes.
export const resolveUserLanguage = ({ accountLanguage, country }: { accountLanguage?: string | null; country?: string | null }): string => {
  const fromAccount = normalizeToSupportedLanguage(accountLanguage);
  if (fromAccount) {
    return fromAccount;
  }

  const fromBrowser = normalizeToSupportedLanguage(getBrowserLanguage());
  if (fromBrowser) {
    return fromBrowser;
  }

  const fromCountry = country ? COUNTRY_TO_LANGUAGE[country.toUpperCase()] : undefined;
  if (fromCountry) {
    return fromCountry;
  }

  return DEFAULT_LANGUAGE;
};

// Called on login: resolve the user's language and, if it differs from the language currently
// rendered, write the cookie and reload once
export const applyResolvedUserLanguage = ({ accountLanguage, country }: { accountLanguage?: string | null; country?: string | null }) => {
  const resolved = resolveUserLanguage({ accountLanguage, country });
  const current = APP_LANGUAGE || initLanguage();
  if (resolved !== current) {
    Cookies.set('__LANGUAGE__', resolved);
    window.location.reload();
  }
};

export const redirectToUpperLevelOfTheNestedURL = (href: string = window.location.href) => {
  const redirectUrl = new URL(href);
  redirectUrl.pathname = redirectUrl.pathname
    .replace(/\/$/, '') // trim the trailing slash
    .replace(/(.*)\/.*/, '$1'); // remove the last level of the URL (aka the param language)
  window.location.replace(redirectUrl.toString());
};

export const useLanguage = () => {
  const language = APP_LANGUAGE || initLanguage();
  const messages = useMemo(() => getMessages(localeData, localeData[language]), [language]);
  return {
    language,
    setLanguage: useCallback((language: string, reload: boolean = true) => {
      Cookies.set('__LANGUAGE__', language);
      if (reload) window.location.reload();
    }, []),
    messages,
  };
};

export const I18nContext = React.createContext<{ language: string; setLanguage: (lang: string, reload?: boolean) => void }>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
});
export const useI18nContext = () => useContext(I18nContext);

export function formatLocaleDate(language: string, date: moment.Moment): string {
  switch (language) {
    case SUPPORTED_BROWSER_LANGUAGE_CODES.english:
      return formatDate(date);

    case SUPPORTED_BROWSER_LANGUAGE_CODES.vietnamese:
      return date.locale(language).format('L');

    case SUPPORTED_BROWSER_LANGUAGE_CODES.korean:
    case SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified:
    case SUPPORTED_BROWSER_LANGUAGE_CODES.chineseTraditional:
      return date.locale(language).format('LL');

    default:
      return date.format('L');
  }
}
